"use client";

import React from "react";
import { Droplets, Building2, Factory, ArrowLeft } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const services = [{
  title: "טיפול סיעודי בבית",
  description: "מתן מענה מקצועי ומסור בבית המטופל, הכולל עזרה בפעולות היומיום, השגחה רפואית ותמיכה רגשית בסביבה המוכרת והנוחה ביותר.",
  icon: Building2,
  link: "/CaregiverServicePagesPage"
}, {
  title: "השגחה בבתי חולים",
  description: "ליווי צמוד ומקצועי במהלך אשפוז, הבטחת בטיחות המטופל, סיוע בתקשורת עם הצוות הרפואי והענקת שקט נפשי מלא לבני המשפחה.",
  icon: Droplets,
  link: "/CaregiverServicePagesPage2"
}, {
  title: "משמרות לילה והשגחה",
  description: "השגחה לילית רציפה המאפשרות למטופל לישון בביטחון ולבני המשפחה לנוח, תוך מענה מיידי לכל צורך או אירוע חריג במהלך הלילה.",
  icon: Factory,
  link: "/NursingCareNowPage"
}];
const faqs = [{
  question: "תוך כמה זמן מגיע מטפל סיעודי בתל אביב?",
  answer: "אנו מתחייבים לספק מטפל סיעודי בתל אביב תוך 5 שעות בלבד מרגע הפנייה. הפריסה הרחבה שלנו בעיר מאפשרת לנו לתת מענה של סיעוד מיידי ללא המתנה מיותרת."
}, {
  question: "האם אתם עובדים עם מטפלים ישראלים?",
  answer: "כן, אנו עובדים עם מטפלים ישראלים בלבד. אנו מאמינים שתקשורת בשפת אם והבנה תרבותית עמוקה הן המפתח לטיפול סיעודי איכותי ומכבד."
}, {
  question: "מה היתרון של שירות סיעוד מיידי?",
  answer: "שירות סיעוד מיידי מיועד למקרים דחופים כמו שחרור פתאומי מבית חולים או צורך בהשגחה לילית דחופה, ומבטיח לכם שקט נפשי וביטחון מלא ברגעים הקריטיים."
}];
export default function ServicesPresentation() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    rootMargin: "0px 0px -50px 0px"
  });
  return <section data-section-id="166074" id="שירותים-בעיר" className="relative w-full py-24 bg-white overflow-hidden">
      {}
      <div className="absolute inset-0 bg-white pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-primary mb-4">
            שירותי הסיעוד המקצועיים שלנו
          </h2>
          <p className="text-slate-500 max-w-2xl mx-auto text-lg font-default">
            מעטפת שירותי סיעוד והשגחה מותאמת אישית, המעניקה ביטחון ואיכות חיים ליקירכם בכל שעות היממה ובכל מקום.
          </p>
          
          <p className="mt-8 text-slate-600 max-w-3xl mx-auto text-lg font-default leading-relaxed">
            כאשר עולה הצורך בשירותי <Link to="#" className="text-emerald-600 hover:underline font-medium">מטפל סיעודי</Link>, המהירות והאיכות הן מעל הכל. ב-Siudy אנו מתמחים במתן פתרונות של <Link to="#" className="text-emerald-600 hover:underline font-medium">סיעוד מיידי</Link>, המעניקים לכם שקט נפשי ברגעים המאתגרים ביותר. אנו מציעים <Link to="#" className="text-emerald-600 hover:underline font-medium">מטפל סיעודי בתל אביב</Link> בזמינות שיא, כמו גם <Link to="#" className="text-emerald-600 hover:underline font-medium">מטפל סיעודי בבית</Link> ושירותי <Link to="/CaregiverServicePagesPage2" className="text-emerald-600 hover:underline font-medium">השגחה בבית חולים</Link> תוך התאמה אישית לכל מטופל. בנוסף, אנו מספקים מענה עבור <Link to="#" className="text-emerald-600 hover:underline font-medium">מטפל סיעודי בירושלים</Link> וגם <Link to="#" className="text-emerald-600 hover:underline font-medium">מטפל סיעודי בחיפה</Link>, תוך הדגשת ה-<Link to="#" className="text-emerald-600 hover:underline font-medium">יתרונות טיפול ביתי</Link> המאפשרים החלמה מיטבית בסביבה תומכת.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12 text-right">
            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 shadow-sm transition-all hover:shadow-md">
              <h3 className="text-xl font-bold text-primary mb-4">הפתרון שלנו</h3>
              <p className="text-slate-500 leading-relaxed">
                אנו מציעים הצבת מטפל סיעודי בתל אביב תוך 5 שעות בלבד, מה שמבטיח מענה מהיר ומקצועי לכל צורך דחוף העולה בבית או בבית החולים.
              </p>
            </div>
            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 shadow-sm transition-all hover:shadow-md">
              <h3 className="text-xl font-bold text-primary mb-4">היתרונות עבורכם</h3>
              <p className="text-slate-500 leading-relaxed">
                אצלנו תמצאו מטפלים ישראלים בלבד, דוברי עברית שוטפת, המעניקים טיפול חם, מסור ומקצועי המבוסס על היכרות עמוקה עם המערכת הרפואית.
              </p>
            </div>
            <div className="p-8 rounded-2xl bg-slate-50 border border-slate-100 shadow-sm transition-all hover:shadow-md">
              <h3 className="text-xl font-bold text-primary mb-4">מידע מפורט</h3>
              <p className="text-slate-500 leading-relaxed">
                שירותי סיעוד מיידי כוללים ליווי צמוד, סיוע בניידות, השגחה רפואית ותמיכה רגשית, הכל תוך הקפדה על הסטנדרטים הגבוהים ביותר של בטיחות.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, i) => {
          const Icon = service.icon;
          return <Card key={i} data-index={i} className={cn("relative overflow-hidden flex flex-col h-full group", "bg-card border border-border rounded-xl py-6", "shadow-sm", "transition-all duration-500 ease-out", "hover:scale-[1.02] hover:shadow-xl hover:border-emerald-500/30", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                {}
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                <CardHeader className="pb-4 relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-emerald-50 flex items-center justify-center mb-6 border border-emerald-100 group-hover:border-emerald-200 transition-colors duration-300">
                    <Icon className="w-7 h-7 text-emerald-600 stroke-[2px]" />
                  </div>
                  <CardTitle className="font-default font-bold text-2xl text-card-foreground tracking-tight">
                    {service.title}
                  </CardTitle>
                </CardHeader>

                <CardContent className="flex-grow relative z-10">
                  <CardDescription className="text-slate-500 text-base leading-relaxed font-default">
                    {service.description}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-6 pb-8 relative z-10">
                  <Link to={service.link} className="inline-flex items-center gap-2 font-default text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors duration-300 group/link">
                    <span className="uppercase tracking-wider">למידע נוסף</span>
                    <ArrowLeft className="w-4 h-4 transform group-hover/link:-translate-x-1 transition-transform duration-300" />
                  </Link>
                </CardFooter>
              </Card>;
        })}
        </div>

        <div className={cn("mt-24 max-w-4xl mx-auto transition-all duration-700 delay-500", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-primary mb-4">שאלות נפוצות</h3>
            <p className="text-slate-500">כל מה שחשוב לדעת על שירותי הסיעוד שלנו בתל אביב</p>
          </div>
          
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqs.map((faq, i) => <AccordionItem key={i} value={`faq-item-${i}`} data-index={i} className="bg-slate-50 border border-slate-100 rounded-xl px-6">
                <AccordionTrigger className="text-right font-bold text-primary hover:no-underline py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-right text-slate-600 text-base leading-relaxed pb-5">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>

          <div className="mt-16 text-center">
            <Button asChild size="lg" className="h-auto py-5 px-10 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xl font-bold shadow-xl shadow-emerald-500/20 transition-all hover:scale-105">
              <Link to="#" className="flex items-center gap-3">
                <span>להזמנת מטפל סיעודי בתל אביב תוך 5 שעות לחצו כאן</span>
                <ArrowLeft className="w-6 h-6" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}